import { useMemo, useState } from 'react';
import PublicDataTable from '../PublicDataTable';
import ContactCell from '../ContactCell';
import { PillTabs } from '../Directory';
import StatusBadge from './OperationalStatusBadge';
import { PROCESSING_CATEGORIES } from '../../data/facilitiesData';

const CATEGORY_TABS = PROCESSING_CATEGORIES.map(({ key, label }) => ({ key, label }));

/**
 * Props
 *   state           string  — selected state filter (from parent)
 *   searchQuery     string  — live search text (from parent)
 *   activeCategory  string  — active processing category key (lifted to parent)
 *   facilityTypes   string[] — selected facility types, [] = all (lifted to parent)
 *   onCategoryChange fn     — callback when sub-tab changes
 *   initialCategory string  — default category on mount
 *   onView          fn(row) — "View" clicked; the page shows FacilityMapView
 */
export default function ProcessingFacilities({
  state,
  searchQuery,
  activeCategory: activeCategoryProp,
  facilityTypes = [],
  onCategoryChange,
  initialCategory,
  onView,
}) {
  // If caller doesn't lift state we fall back to internal state
  const [internalCategory, setInternalCategory] = useState(initialCategory || PROCESSING_CATEGORIES[0].key);
  const activeCategory = activeCategoryProp ?? internalCategory;

  const category = PROCESSING_CATEGORIES.find((item) => item.key === activeCategory) || PROCESSING_CATEGORIES[0];

  const rows = useMemo(() => {
    return category.rows.filter((row) => {
      const matchesState = state === 'All States' || row.state === state;
      const matchesType  = !facilityTypes.length || facilityTypes.includes(row.type);
      return matchesState && matchesType;
    });
  }, [category, state, facilityTypes]);

  function handleCategoryChange(key) {
    if (onCategoryChange) onCategoryChange(key);
    else setInternalCategory(key);
  }

  // Action column hands the row to the page, which shows FacilityMapView
  const columns = useMemo(() => [
    { key: 'name',     title: 'Facility Name' },
    { key: 'type',     title: 'Facility Type' },
    { key: 'species',  title: 'Species' },
    { key: 'products', title: 'Products Processed' },
    {
      key: 'capacity',
      title: 'Capacity',
      render: (v) => (v != null ? Number(v).toLocaleString('en-NG') : '—'),
    },
    { key: 'unit', title: 'Unit' },
    {
      key: 'status',
      title: 'Operational Status',
      render: (v) => <StatusBadge status={v || '—'} />,
    },
    { key: 'contact', title: 'Contact', render: (v) => <ContactCell number={v} /> },
    {
      key: '__action',
      title: 'Action',
      render: (_, row) => (
        <button
          type="button"
          onClick={() => onView(row)}
          className="text-[11px] font-medium text-[#28ba12] underline underline-offset-2 hover:no-underline"
        >
          View
        </button>
      ),
    },
  ], [onView]);

  return (
    <div>
      {/* Sub-category pills — Figma 498:48375 */}
      <PillTabs tabs={CATEGORY_TABS} active={activeCategory} onChange={handleCategoryChange} />

      <div className="mt-4">
        <PublicDataTable
          key={activeCategory}
          columns={columns}
          data={rows}
          searchQuery={searchQuery}
          pageSize={10}
        />
      </div>
    </div>
  );
}
